import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/sections/Footer';
import { FreeSecurityChecks } from '@/components/sections/FreeSecurityChecks';
import { LiveThreatIntelligence } from '@/components/defend/LiveThreatIntelligence';
import { ScrollReveal } from '@/components/effects/ScrollReveal';
import ServiceCalloutBanner from '@/components/shared/ServiceCalloutBanner';
import { Button } from '@/components/ui/button';
import {
  ArrowRight,
  ArrowDown,
  ShieldAlert,
  Eye,
  Radar,
  Cloud,
  FileCheck,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { useSEO, SITE_URL } from '@/hooks/useSEO';

const CARD_STYLE = {
  background: 'var(--l1)',
  border: '1px solid var(--b1)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.02) inset',
};

const STATS = [
  { value: '50%',     label: 'of UK businesses were breached in 2025', tone: 'danger', source: 'NCSC' },
  { value: '£8,460',  label: 'average cost of an SME breach',           tone: 'warn' },
  { value: '197',     label: 'days average to detect a breach without monitoring', tone: 'danger' },
  { value: '67%',     label: 'of SMEs have no formal security plan',   tone: 'warn' },
];

const SERVICES = [
  {
    icon: Eye,
    title: '24/7 SOC Monitoring and Managed Detection',
    body: 'Continuous threat detection by expert analysts before attacks reach your systems. No gaps. No delays. No surprises.',
  },
  {
    icon: Radar,
    title: 'Vulnerability Assessment and Penetration Testing',
    body: 'We attack your systems the same way a real attacker would — then give you a full report and remediation plan. Know your weaknesses before your adversaries do.',
  },
  {
    icon: Cloud,
    title: 'Cloud Security — AWS, Azure, GCP, Microsoft 365',
    body: 'Misconfigured cloud is the single biggest cause of SME breaches. We audit your entire cloud environment and lock it down properly.',
  },
  {
    icon: FileCheck,
    title: 'Compliance — Cyber Essentials, ISO 27001, GDPR',
    body: 'Compliance is not just a certificate. It is proof that your security posture is real. We guide you from gap analysis to certification.',
  },
];

const ACTIVE_GUARD_FEATURES = [
  '24/7 monitoring',
  'Weekly AI security briefing',
  'Instant alerts on critical findings',
  'Full scan history',
  'Up to 3 monitored domains',
  'Cancel any time',
];

function scrollTo(id) {
  return (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
}

/**
 * Defend threat-reality stat card.
 * Spec constraints:
 *  - value font-size: clamp(1.5rem, 3vw, 2.2rem) — never larger, avoids overflow
 *  - label: 0.72rem DM Sans
 *  - padding: 1.5rem, overflow: hidden, no fixed min-height
 *  - very dark card bg with red undertone; number in var(--danger)
 *  - hover: subtle red border + bottom accent line (danger → purple)
 */
function StatCard({ value, label, tone, source }) {
  return (
    <div
      className="relative rounded-lg transition-colors group"
      style={{
        padding: '1.5rem',
        background: 'rgba(8,4,4,0.95)',
        border: '1px solid rgba(248,113,113,0.15)',
        overflow: 'hidden',
        boxShadow: '0 1px 0 rgba(255,255,255,0.02) inset',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'rgba(248,113,113,0.35)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(248,113,113,0.15)'; }}
    >
      <p
        className="font-display font-extrabold mb-2"
        style={{
          fontSize: 'clamp(1.5rem, 3vw, 2.2rem)',
          color: 'var(--danger)',
          lineHeight: 1.05,
          wordBreak: 'keep-all',
        }}
      >
        {value}
      </p>
      <p
        className="font-body"
        style={{ fontSize: '0.72rem', color: 'rgba(240,237,248,0.78)', lineHeight: 1.5 }}
      >
        {label}
      </p>
      {source && (
        <p
          className="font-mono uppercase tracking-[0.15em] mt-2"
          style={{ fontSize: '0.62rem', color: 'var(--ts)' }}
        >
          source · {source}
        </p>
      )}
      {/* hover accent bar: danger → purple */}
      <span
        aria-hidden
        className="absolute bottom-0 left-0 right-0 origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500"
        style={{ height: 2, background: 'linear-gradient(90deg, var(--danger), var(--a))' }}
      />
    </div>
  );
}

const ACTIVE_GUARD_SERVICE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Acodia Watch',
  serviceType: 'Cybersecurity monitoring',
  provider: {
    '@type': 'Organization',
    name: 'Acodia',
    url: 'https://acodia.co.uk',
  },
  areaServed: { '@type': 'Country', name: 'United Kingdom' },
  description:
    'Continuous security monitoring for SME websites. Acodia Watch runs scheduled deep scans, monitors for new CVEs and breach exposures, and sends real-time alerts the moment something changes.',
  url: 'https://acodia.co.uk/watch',
  offers: [
    { '@type': 'Offer', name: 'Acodia Watch Essential', price: '49', priceCurrency: 'GBP', url: 'https://acodia.co.uk/watch?plan=essential' },
    { '@type': 'Offer', name: 'Acodia Watch Professional', price: '99', priceCurrency: 'GBP', url: 'https://acodia.co.uk/watch?plan=professional' },
    { '@type': 'Offer', name: 'Acodia Watch Business', price: '199', priceCurrency: 'GBP', url: 'https://acodia.co.uk/watch?plan=business' },
  ],
};

export default function DefendPage() {
  useSEO({
    title: 'Defend | Acodia — Grow. Defend. Be Found.',
    description: 'Enterprise-grade cybersecurity for growing businesses. Acodia Watch monitoring, vulnerability assessment, cloud security, and compliance support.',
    path: '/defend',
    ogImage: `${SITE_URL}/images/og/og-defend.png`,
    jsonLd: [ACTIVE_GUARD_SERVICE_SCHEMA],
  });

  return (
    <div style={{ background: 'rgba(3,3,8,1)', minHeight: '100vh', color: 'var(--white)' }} data-testid="defend-page">
      <Navbar />

      {/* ── HERO — single-column editorial layout matching Learn/Launch/Reach ── */}
      <section
        data-testid="defend-hero"
        className="relative pt-32 pb-20 sm:pt-40 sm:pb-28"
        style={{ background: 'var(--bg)' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'var(--hero-mesh)' }} />
        <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <p className="font-body text-xs uppercase tracking-[0.22em] mb-3" style={{ color: 'var(--a)' }}>
              Acodia Defend
            </p>
            <h1
              className="font-display font-extrabold tracking-[-0.03em] mb-5 max-w-[22ch]"
              style={{ fontSize: 'clamp(2.25rem, 5vw, 4rem)', color: '#F0EDF8' }}
            >
              Your Business Is Being{' '}
              <span style={{ color: 'var(--a)' }}>Targeted</span>{' '}
              Right Now. Are You Protected?
            </h1>
            <p
              className="font-body font-light text-base sm:text-lg max-w-3xl mb-8"
              style={{ color: 'var(--tm)', lineHeight: '1.72' }}
            >
              Most small businesses find out they have a security problem after the breach. Acodia Defend finds your
              vulnerabilities before attackers do — and keeps them out for good. Enterprise-grade protection built for
              growing businesses.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="/pricing?service=defend"
                data-testid="defend-hero-primary"
                className="btn-shimmer inline-flex items-center font-body text-sm h-11 px-6 rounded-[3px]"
                style={{ background: 'var(--a)', color: 'var(--white)' }}
              >
                Get a Free Security Assessment <ArrowRight size={14} className="ml-2" />
              </a>
              <Button
                data-testid="defend-hero-secondary"
                onClick={scrollTo('what-we-monitor')}
                className="font-body text-sm h-11 px-6 rounded-[3px]"
                style={{ background: 'transparent', color: 'var(--tm)', border: '1px solid var(--b1)' }}
              >
                See What We Monitor <ArrowDown size={14} className="ml-2" />
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── WHAT WE MONITOR (full-width LTI — anchor for hero secondary CTA) ── */}
      <section id="what-we-monitor" data-testid="defend-what-we-monitor" className="py-14 sm:py-20" style={{ background: 'var(--l1)' }}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <LiveThreatIntelligence />
          </ScrollReveal>
        </div>
      </section>

      {/* ── THREAT REALITY ── */}
      <section data-testid="defend-reality" className="py-14 sm:py-20" style={{ background: 'var(--l2)' }}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-10 max-w-3xl mx-auto">
              <h2
                className="font-display font-extrabold tracking-[-0.03em] mb-4"
                style={{ fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', color: 'var(--white)' }}
              >
                The Threat Is Real. The Solution Does Not Have to Be Expensive.
              </h2>
            </div>
          </ScrollReveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STATS.map((s, i) => (
              <ScrollReveal key={i} delay={i * 0.08}>
                <StatCard {...s} />
              </ScrollReveal>
            ))}
          </div>
          <ScrollReveal>
            <p
              className="font-body text-base max-w-2xl mx-auto text-center mt-10"
              style={{ color: 'var(--tm)', lineHeight: '1.72' }}
            >
              These are not enterprise problems. They are yours. And they are preventable.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* ── SERVICES BREAKDOWN — very dark, slightly stronger purple borders ── */}
      <section data-testid="defend-services" className="py-14 sm:py-20" style={{ background: 'rgba(4,3,8,1)' }}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <h2
              className="font-display font-extrabold tracking-[-0.03em] mb-10 max-w-[24ch]"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', color: '#F0EDF8' }}
            >
              Everything You Need. Nothing You Do Not.
            </h2>
          </ScrollReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {SERVICES.map(({ icon: Icon, title, body }, i) => (
              <ScrollReveal key={title} delay={i * 0.06}>
                <div
                  className="p-6 rounded-lg h-full transition-all"
                  style={{
                    background: 'rgba(8,4,16,0.88)',
                    border: '1px solid rgba(139,92,246,0.12)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(139,92,246,0.28)';
                    e.currentTarget.style.boxShadow = '0 0 40px rgba(139,92,246,0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = 'rgba(139,92,246,0.12)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <div
                    className="flex items-center justify-center rounded mb-4"
                    style={{
                      width: 40,
                      height: 40,
                      background: 'rgba(139,92,246,0.1)',
                      border: '1px solid rgba(139,92,246,0.2)',
                    }}
                  >
                    <Icon size={18} style={{ color: 'var(--a)' }} />
                  </div>
                  <h3 className="font-display text-lg font-semibold mb-2" style={{ color: '#F0EDF8' }}>
                    {title}
                  </h3>
                  <p className="font-body text-sm" style={{ color: 'var(--tm)', lineHeight: '1.65' }}>
                    {body}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── FREE SECURITY CHECKS — Brand Impersonation Watch + Domain teaser (replaced coming-soon waitlist 2026-06-05) ── */}
      <section data-testid="defend-scanner" className="pt-14 sm:pt-20" style={{ background: 'var(--bg)' }}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 mb-6">
          <ScrollReveal>
            <h2
              className="font-display font-extrabold tracking-[-0.03em] mb-4 max-w-[24ch]"
              style={{ fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', color: 'var(--white)' }}
            >
              Free Security Checks.
            </h2>
            <p className="font-body font-light text-base max-w-3xl" style={{ color: 'var(--tm)', lineHeight: '1.72' }}>
              Two free tools — spot brand-impersonation domains and snapshot your domain's security posture.
              Public data only, no scanning involved.
            </p>
          </ScrollReveal>
        </div>
        <FreeSecurityChecks hideHeading />
      </section>

      <section className="py-8 sm:py-10" style={{ background: 'var(--bg)' }}>
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8">
          <ServiceCalloutBanner
            testId="defend-callout-banner"
            text="Need our team to handle this for you? Acodia Defend offers hands-on security services."
            ctaText="Book a free consultation →"
            ctaUrl="/pricing?service=defend"
          />
        </div>
      </section>

      {/* ── ACODIA WATCH BLOCK — premium feel with purple glow ── */}
      <section data-testid="defend-active-guard" className="py-14 sm:py-20" style={{ background: 'rgba(4,3,8,1)' }}>
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div
              className="p-8 rounded-lg"
              style={{
                background: 'linear-gradient(135deg, rgba(8,4,16,1) 0%, rgba(4,3,8,1) 100%)',
                border: '1px solid rgba(139,92,246,0.3)',
                boxShadow: '0 0 60px rgba(139,92,246,0.08)',
              }}
            >
              <div className="flex items-start gap-3 mb-3">
                <ShieldAlert size={22} style={{ color: 'var(--a)', marginTop: 4 }} />
                <p className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: 'var(--a)' }}>
                  Flagship · Acodia Watch
                </p>
              </div>
              <h2
                className="font-display font-extrabold tracking-[-0.03em] mb-4 max-w-[24ch]"
                style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', color: '#F0EDF8' }}
              >
                Acodia Watch — Continuous Protection from £49/month.
              </h2>
              <p className="font-body font-light text-base max-w-3xl mb-6" style={{ color: 'rgba(240,237,248,0.78)', lineHeight: '1.72' }}>
                Most security services charge enterprise prices for enterprise clients. Acodia Watch was built to give
                growing businesses the same level of protection at a fraction of the cost.
              </p>

              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-6 max-w-2xl">
                {ACTIVE_GUARD_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2 font-body text-sm" style={{ color: 'rgba(240,237,248,0.78)' }}>
                    <CheckCircle2 size={14} style={{ color: 'var(--ok)', marginTop: 3, flexShrink: 0 }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <p className="font-mono text-sm mb-4" style={{ color: 'rgba(240,237,248,0.7)' }}>
                7-day free trial · card required · cancel any time
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { plan: 'essential', label: 'Essential', price: '£49', testId: 'defend-tier-essential' },
                  { plan: 'professional', label: 'Professional', price: '£99', testId: 'defend-tier-professional', popular: true },
                  { plan: 'business', label: 'Business', price: '£199', testId: 'defend-tier-business' },
                ].map((tier) => (
                  <a
                    key={tier.plan}
                    href={`/auth?plan=${tier.plan}`}
                    data-testid={tier.testId}
                    className="btn-shimmer inline-flex items-center justify-between font-body text-sm h-12 px-4 rounded-[3px] transition-all"
                    style={{
                      background: tier.popular ? 'var(--a)' : 'rgba(139,92,246,0.12)',
                      color: 'var(--white)',
                      border: tier.popular ? 'none' : '1px solid rgba(139,92,246,0.3)',
                    }}
                  >
                    <span className="flex flex-col items-start leading-tight">
                      <span className="font-mono text-[10px] uppercase tracking-[0.18em] opacity-80">
                        Start {tier.label} Plan
                      </span>
                      <span className="font-display text-sm font-bold">{tier.price}/mo</span>
                    </span>
                    <ArrowRight size={14} />
                  </a>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── FINAL CTA — red-tinted urgency glow ── */}
      <section
        data-testid="defend-cta"
        className="py-16 sm:py-24 relative"
        style={{
          background: 'rgba(3,3,8,1)',
          boxShadow: 'inset 0 0 120px rgba(248,113,113,0.06)',
        }}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <ScrollReveal>
            <AlertTriangle size={28} style={{ color: 'var(--warn)', margin: '0 auto 16px' }} />
            <h2
              className="font-display font-extrabold tracking-[-0.03em] mb-4"
              style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', color: '#F0EDF8' }}
            >
              Not Sure What Level of Protection You Need?
            </h2>
            <p className="font-body font-light text-base mb-8" style={{ color: 'var(--tm)', lineHeight: '1.72' }}>
              Answer a few questions and we will tell you exactly what we recommend — and what it will cost.
            </p>
            <a
              href="/pricing?service=defend"
              data-testid="defend-cta-button"
              className="btn-shimmer inline-flex items-center font-body text-sm h-12 px-7 rounded-[3px]"
              style={{ background: 'var(--a)', color: 'var(--white)' }}
            >
              Get My Security Recommendation <ArrowRight size={14} className="ml-2" />
            </a>
          </ScrollReveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
